<!-- ModalNuevoDiseno.vue — Alta y edición de una preaprobación de diseños -->
<!--
  El mismo modal sirve para crear y editar. En edición los PDFs se reemplazan
  completos, no se editan uno por uno: mientras no se notifica el registro es
  un borrador, y volver a subir el juego es más claro que llevar altas y bajas.
  Editar solo se permite antes de notificar; después el registro es evidencia.
-->
<template>
  <div class="modal fade" id="modalNuevoDiseno" tabindex="-1" ref="el">
    <div class="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable">
      <div class="modal-content modal-divisan border-0">
        <div class="modal-header border-0 pb-2">
          <h5 class="header-titulo">
            <i class="bi bi-vector-pen me-2"></i>
            {{ esEdicion ? "Editar preaprobación" : "Nueva preaprobación de diseños" }}
          </h5>
          <button
            type="button"
            class="btn-close"
            data-bs-dismiss="modal"
            :disabled="guardando"
          ></button>
        </div>

        <div class="modal-body pt-2">
          <div class="row g-3">
            <!-- Cotización -->
            <div class="col-12 col-md-4">
              <label class="campo-label">
                Cotización <span class="text-danger">*</span>
              </label>
              <input
                v-model="cotizacion"
                type="text"
                class="campo-input"
                placeholder="COT-2026-0184"
                :disabled="guardando"
              />
            </div>

            <!-- Cliente / Contacto de venta al público -->
            <div class="col-12 col-md-8" v-click-outside="cerrarCliente">
              <div class="d-flex justify-content-between align-items-center mb-1">
                <label class="campo-label mb-0">
                  {{ ventaPublico ? "Contacto (venta al público)" : "Cliente" }}
                  <span class="text-danger">*</span>
                </label>
                <button
                  type="button"
                  class="switch-venta-publico"
                  :class="{ activo: ventaPublico }"
                  :disabled="guardando"
                  @click="ventaPublico = !ventaPublico"
                >
                  <span class="switch-track"><span class="switch-thumb"></span></span>
                  <span class="switch-txt">Venta al público</span>
                </button>
              </div>

              <template v-if="!ventaPublico">
                <div class="select-wrapper">
                  <input
                    v-model="clienteBusqueda"
                    type="text"
                    class="campo-input select-input"
                    :class="{ 'tiene-valor': clienteId && !clienteBusqueda }"
                    :placeholder="clienteId ? nombreSeleccionado : 'Buscar cliente...'"
                    :disabled="guardando"
                    @focus="abierto = true"
                    @input="abierto = true"
                  />
                  <i
                    class="bi select-arrow"
                    :class="abierto ? 'bi-chevron-up' : 'bi-chevron-down'"
                    @mousedown.prevent="abierto = !abierto"
                  ></i>
                  <div v-if="abierto" class="dropdown-lista">
                    <div v-if="clientesFiltrados.length === 0" class="dropdown-vacio">
                      Sin resultados
                    </div>
                    <div
                      v-for="c in clientesFiltrados"
                      :key="c.id"
                      class="dropdown-item-cli"
                      :class="{ selected: clienteId === c.id }"
                      @mousedown.prevent="elegirCliente(c)"
                    >
                      <span class="item-codigo">{{ c.numero_cliente }}</span>
                      <span class="item-nombre">{{ c.nombre }}</span>
                    </div>
                  </div>
                </div>
                <small v-if="clienteSinTelefono" class="aviso-tel">
                  <i class="bi bi-exclamation-triangle-fill me-1"></i>
                  Este cliente no tiene teléfono registrado; no se le podrá notificar.
                </small>
              </template>

              <!-- Se guarda en el propio registro, no en el catálogo: así dos ventas
                   de mostrador en proceso al mismo tiempo no se pisan el contacto. -->
              <div v-else class="row g-2">
                <div class="col-12">
                  <input
                    v-model="contactoNombre"
                    type="text"
                    class="campo-input"
                    placeholder="Nombre del cliente"
                    :disabled="guardando"
                  />
                </div>
                <div class="col-12" v-click-outside="cerrarEstado">
                  <div class="select-wrapper">
                    <input
                      v-model="estadoBusqueda"
                      type="text"
                      class="campo-input select-input"
                      :class="{ 'tiene-valor': contactoEstado && !estadoBusqueda }"
                      :placeholder="contactoEstado ? estadoLabelSeleccionado : 'Selecciona un estado...'"
                      :disabled="guardando"
                      @focus="estadoAbierto = true"
                      @input="estadoAbierto = true"
                    />
                    <i
                      class="bi select-arrow"
                      :class="estadoAbierto ? 'bi-chevron-up' : 'bi-chevron-down'"
                      @mousedown.prevent="estadoAbierto = !estadoAbierto"
                    ></i>
                    <div v-if="estadoAbierto" class="dropdown-lista dropdown-estado">
                      <div v-if="estadosFiltrados.length === 0" class="dropdown-vacio">
                        Sin resultados
                      </div>
                      <div
                        v-for="e in estadosFiltrados"
                        :key="e.value"
                        class="dropdown-item-cli"
                        :class="{ selected: contactoEstado === e.value }"
                        @mousedown.prevent="elegirEstado(e)"
                      >
                        {{ e.label }}
                      </div>
                    </div>
                  </div>
                </div>
                <div class="col-12">
                  <div class="telefono-lada">
                    <select
                      v-model="contactoPrefijo"
                      class="lada-select"
                      :disabled="guardando"
                    >
                      <option v-for="l in LADAS" :key="l.codigo" :value="l.codigo">
                        {{ l.bandera }} {{ l.codigo }}
                      </option>
                    </select>
                    <input
                      v-model="contactoTelefonoInput"
                      type="text"
                      inputmode="numeric"
                      class="campo-input tel-input"
                      :placeholder="'8448935204'.slice(0, contactoMaxLongitud)"
                      :disabled="guardando"
                    />
                  </div>
                  <small
                    v-if="contactoTelefono && contactoTelefono.length !== contactoMaxLongitud"
                    class="aviso-tel"
                  >
                    <i class="bi bi-exclamation-triangle-fill me-1"></i>
                    Debe tener {{ contactoMaxLongitud }} dígitos — tiene {{ contactoTelefono.length }}.
                  </small>
                  <small v-else class="aviso-tel-ok">
                    <i class="bi bi-whatsapp me-1"></i>
                    Solo dígitos, sin espacios ni guiones ({{ contactoTelefono.length }}/{{ contactoMaxLongitud }}).
                  </small>
                </div>
              </div>
            </div>
          </div>

          <!-- Carga de PDFs -->
          <div class="mt-4">
            <label class="campo-label">
              Diseños (PDF) <span class="text-danger">*</span>
              <span class="contador" :class="{ tope: totalDocumentos >= MAX }">
                {{ totalDocumentos }}/{{ MAX }}
              </span>
            </label>

            <!-- Documentos ya guardados: se quitan uno por uno, como en Proyectos -->
            <div v-if="pdfsGuardados.length" class="lista-archivos mb-2">
              <div
                v-for="pdf in pdfsGuardados"
                :key="pdf.id"
                class="archivo-row guardado"
              >
                <i class="bi bi-file-earmark-pdf-fill text-danger"></i>
                <a
                  :href="pdf.archivo"
                  target="_blank"
                  rel="noopener"
                  class="archivo-nombre"
                >
                  {{ pdf.nombre_original }}
                </a>
                <button
                  class="btn-quitar"
                  :disabled="guardando || eliminando === pdf.id"
                  title="Eliminar documento"
                  @click="eliminarGuardado(pdf)"
                >
                  <span
                    v-if="eliminando === pdf.id"
                    class="spinner-border spinner-border-sm"
                  ></span>
                  <i v-else class="bi bi-trash3"></i>
                </button>
              </div>
            </div>

            <div
              class="upload-zone"
              :class="{ dragging: arrastrando, deshabilitado: totalDocumentos >= MAX }"
              @click="totalDocumentos < MAX && inputArchivos.click()"
              @dragover.prevent="arrastrando = true"
              @dragleave.prevent="arrastrando = false"
              @drop.prevent="onDrop"
            >
              <input
                ref="inputArchivos"
                type="file"
                accept="application/pdf"
                multiple
                class="d-none"
                @change="onSeleccionar"
              />
              <div class="upload-inner">
                <i class="bi bi-file-earmark-plus upload-icon"></i>
                <div>
                  <p class="fw-bold text-cafe mb-0">Arrastra los PDFs aquí</p>
                  <small class="text-muted">
                    o haz clic para seleccionar · máximo {{ MAX }} archivos
                  </small>
                </div>
              </div>
            </div>

            <div v-if="archivos.length" class="lista-archivos">
              <div v-for="(a, idx) in archivos" :key="idx" class="archivo-row">
                <i class="bi bi-file-earmark-pdf-fill text-danger"></i>
                <span class="archivo-nombre">{{ a.name }}</span>
                <span class="archivo-size">{{ formatSize(a.size) }}</span>
                <button
                  class="btn-quitar"
                  :disabled="guardando"
                  title="Quitar"
                  @click="archivos.splice(idx, 1)"
                >
                  <i class="bi bi-x-lg"></i>
                </button>
              </div>
            </div>

            <div v-if="error" class="error-box">
              <i class="bi bi-exclamation-triangle-fill me-2"></i>{{ error }}
            </div>
          </div>
        </div>

        <div class="modal-footer border-0 pt-0">
          <button class="btn btn-cancelar" data-bs-dismiss="modal" :disabled="guardando">
            Cancelar
          </button>
          <button
            class="btn btn-guardar"
            :disabled="guardando || !puedeGuardar"
            :title="motivoBloqueo"
            @click="handleGuardar"
          >
            <span v-if="guardando" class="spinner-border spinner-border-sm me-2"></span>
            {{
              guardando
                ? "Guardando..."
                : esEdicion
                ? "Guardar cambios"
                : "Crear registro"
            }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from "vue";
import { Modal } from "bootstrap";
import Swal from "sweetalert2";
import { crearDiseno, actualizarDiseno, eliminarPdfDiseno } from "@/utils/api_backend.js";
import { ESTADOS_MEXICO } from "@/utils/estadosMexico";
import { LADAS, LADA_LONGITUD, soloDigitos, detectarLada } from "@/utils/ladas";

const MAX = 15;

const props = defineProps({
  catClientes: { type: Array, default: () => [] },
});
const emit = defineEmits(["guardado"]);

const el = ref(null);
let modalInstance = null;

const registroId = ref(null);
const cotizacion = ref("");
const clienteId = ref(null);
const archivos = ref([]); // pendientes de subir
const pdfsGuardados = ref([]); // ya persistidos en el registro
const guardando = ref(false);
const eliminando = ref(null);
const error = ref("");

const inputArchivos = ref(null);
const arrastrando = ref(false);

// ── Venta al público general ────────────────────────────────
const ventaPublico = ref(false);
const contactoNombre = ref("");
const contactoEstado = ref("");
const contactoPrefijoRaw = ref("+52");
const contactoTelefono = ref(""); // solo dígitos, sin la lada

const contactoMaxLongitud = computed(() => LADA_LONGITUD[contactoPrefijoRaw.value] || 10);

// v-model del input: filtra en cada tecleo/pegado y trunca a la longitud
// de la lada activa, igual que el teléfono del catálogo de clientes.
const contactoTelefonoInput = computed({
  get: () => contactoTelefono.value,
  set: (val) => {
    contactoTelefono.value = soloDigitos(val).slice(0, contactoMaxLongitud.value);
  },
});

// v-model del select de lada: al cambiarla, re-trunca el número ya escrito
// a la nueva longitud (mismo trato que el teléfono del catálogo de clientes).
const contactoPrefijo = computed({
  get: () => contactoPrefijoRaw.value,
  set: (val) => {
    contactoPrefijoRaw.value = val;
    contactoTelefono.value = soloDigitos(contactoTelefono.value).slice(
      0,
      LADA_LONGITUD[val] || 10
    );
  },
});

// ── Dropdown de estado (búsqueda + máximo 5 renglones visibles) ──
const estadoBusqueda = ref("");
const estadoAbierto = ref(false);

const estadosFiltrados = computed(() => {
  const q = estadoBusqueda.value.trim().toLowerCase();
  if (!q) return ESTADOS_MEXICO;
  return ESTADOS_MEXICO.filter(
    (e) => e.label.toLowerCase().includes(q) || e.value.toLowerCase().includes(q)
  );
});

const estadoLabelSeleccionado = computed(
  () => ESTADOS_MEXICO.find((e) => e.value === contactoEstado.value)?.label || ""
);

function elegirEstado(e) {
  contactoEstado.value = e.value;
  estadoAbierto.value = false;
  estadoBusqueda.value = "";
}
function cerrarEstado() {
  estadoAbierto.value = false;
  estadoBusqueda.value = "";
}

// ── Dropdown de cliente ────────────────────────────────────
const clienteBusqueda = ref("");
const abierto = ref(false);

const clientesFiltrados = computed(() => {
  const q = clienteBusqueda.value.toLowerCase();
  return props.catClientes
    .filter(
      (c) =>
        c.nombre.toLowerCase().includes(q) ||
        String(c.numero_cliente).toLowerCase().includes(q)
    )
    .slice(0, 50);
});

const clienteSeleccionado = computed(() =>
  props.catClientes.find((c) => c.id === clienteId.value)
);
const nombreSeleccionado = computed(() =>
  clienteSeleccionado.value
    ? `${clienteSeleccionado.value.numero_cliente} — ${clienteSeleccionado.value.nombre}`
    : ""
);
// Se avisa aquí y no al notificar: mejor enterarse antes de subir 15 PDFs.
const clienteSinTelefono = computed(
  () => !!clienteSeleccionado.value && !clienteSeleccionado.value.telefono
);

function elegirCliente(c) {
  clienteId.value = c.id;
  clienteBusqueda.value = "";
  abierto.value = false;
}
function cerrarCliente() {
  abierto.value = false;
}

// ── Estado del formulario ──────────────────────────────────
const esEdicion = computed(() => registroId.value !== null);

// El tope de 15 cuenta guardados + pendientes, igual que en el backend.
const totalDocumentos = computed(
  () => pdfsGuardados.value.length + archivos.value.length
);

const puedeGuardar = computed(() => {
  if (!cotizacion.value.trim()) return false;
  if (ventaPublico.value) {
    if (
      !contactoNombre.value.trim() ||
      contactoTelefono.value.length !== contactoMaxLongitud.value
    )
      return false;
  } else if (!clienteId.value) {
    return false;
  }
  // Un registro no puede quedarse sin documentos: sin ellos no hay nada
  // que mandarle al cliente.
  return totalDocumentos.value > 0;
});

const motivoBloqueo = computed(() => {
  if (!cotizacion.value.trim()) return "Captura el número de cotización";
  if (ventaPublico.value) {
    if (!contactoNombre.value.trim()) return "Captura el nombre del cliente";
    if (contactoTelefono.value.length !== contactoMaxLongitud.value) {
      return `Captura un teléfono a ${contactoMaxLongitud.value} dígitos`;
    }
  } else if (!clienteId.value) {
    return "Selecciona el cliente";
  }
  if (!totalDocumentos.value) return "Adjunta al menos un PDF";
  return "";
});

// ── Ciclo de vida ──────────────────────────────────────────
onMounted(() => {
  modalInstance = new Modal(el.value);
});

function abrir(registro = null) {
  registroId.value = registro?.id ?? null;
  cotizacion.value = registro?.cotizacion ?? "";
  clienteId.value = registro?.cliente_pk ?? buscarClientePorNumero(registro) ?? null;
  ventaPublico.value = !!registro?.venta_publico;
  contactoNombre.value = registro?.contacto_nombre ?? "";
  contactoEstado.value = registro?.contacto_estado ?? "";
  // Se guarda como "+52 XXXXXXXXXX": se detecta la lada por prefijo numérico
  // (igual que en el catálogo de clientes) y el resto son los dígitos locales.
  {
    const digitos = soloDigitos(registro?.contacto_telefono);
    const prefijo = digitos ? detectarLada(digitos) : "+52";
    contactoPrefijoRaw.value = prefijo;
    contactoTelefono.value = digitos ? digitos.slice(prefijo.length - 1) : "";
  }
  pdfsGuardados.value = [...(registro?.pdfs || [])];
  archivos.value = [];
  eliminando.value = null;
  clienteBusqueda.value = "";
  abierto.value = false;
  estadoBusqueda.value = "";
  estadoAbierto.value = false;
  arrastrando.value = false;
  error.value = "";
  guardando.value = false;
  modalInstance.show();
}
defineExpose({ abrir });

// El serializer devuelve el cliente por nombre y número, no por id: se
// resuelve contra el catálogo para preseleccionarlo al editar.
function buscarClientePorNumero(registro) {
  if (!registro?.cliente_numero) return null;
  const encontrado = props.catClientes.find(
    (c) => String(c.numero_cliente) === String(registro.cliente_numero)
  );
  return encontrado?.id ?? null;
}

// ── Archivos ───────────────────────────────────────────────
function onSeleccionar(e) {
  agregar([...e.target.files]);
  e.target.value = "";
}
function onDrop(e) {
  arrastrando.value = false;
  agregar([...e.dataTransfer.files]);
}

function agregar(nuevos) {
  error.value = "";

  const noPdf = nuevos.filter((f) => f.type !== "application/pdf");
  if (noPdf.length) {
    error.value = `Solo se aceptan PDFs. Se ignoró: ${noPdf
      .map((f) => f.name)
      .join(", ")}`;
  }

  const pdfs = nuevos.filter((f) => f.type === "application/pdf");
  const yaPuestos = archivos.value.map((a) => a.name);
  const sinDuplicados = pdfs.filter((f) => !yaPuestos.includes(f.name));

  const espacio = MAX - totalDocumentos.value;
  if (sinDuplicados.length > espacio) {
    error.value = `Solo caben ${MAX} archivos; se tomaron los primeros ${espacio}.`;
  }

  archivos.value.push(...sinDuplicados.slice(0, espacio));
}

// Se borra de una vez contra el servidor, no al guardar: así el contador y el
// espacio disponible reflejan la realidad mientras el usuario sigue editando.
async function eliminarGuardado(pdf) {
  eliminando.value = pdf.id;
  try {
    await eliminarPdfDiseno(registroId.value, pdf.id);
    pdfsGuardados.value = pdfsGuardados.value.filter((p) => p.id !== pdf.id);
    emit("guardado");
  } catch (err) {
    error.value = err.response?.data?.detail || "No se pudo eliminar el documento.";
  } finally {
    eliminando.value = null;
  }
}

function formatSize(bytes) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

// ── Guardar ────────────────────────────────────────────────
async function handleGuardar() {
  guardando.value = true;
  error.value = "";
  try {
    const payload = {
      cotizacion: cotizacion.value.trim(),
      clienteId: clienteId.value,
      archivos: archivos.value,
      ventaPublico: ventaPublico.value,
      contactoNombre: contactoNombre.value.trim(),
      contactoEstado: contactoEstado.value,
      contactoTelefono: contactoTelefono.value
        ? `${contactoPrefijo.value} ${contactoTelefono.value}`
        : "",
    };

    if (esEdicion.value) {
      await actualizarDiseno(registroId.value, payload);
    } else {
      await crearDiseno(payload);
    }

    modalInstance.hide();
    emit("guardado");
    Swal.fire({
      icon: "success",
      title: esEdicion.value ? "Registro actualizado" : "Registro creado",
      timer: 1500,
      showConfirmButton: false,
    });
  } catch (err) {
    const datos = err.response?.data;
    error.value =
      datos?.archivos?.[0] ||
      datos?.cotizacion?.[0] ||
      datos?.cliente_id?.[0] ||
      datos?.contacto_nombre?.[0] ||
      datos?.contacto_telefono?.[0] ||
      datos?.venta_publico?.[0] ||
      datos?.detail ||
      "No se pudo guardar el registro.";
  } finally {
    guardando.value = false;
  }
}

// ── Directiva click-outside ────────────────────────────────
const vClickOutside = {
  mounted(elemento, binding) {
    elemento._co = (e) => {
      if (!elemento.contains(e.target)) binding.value(e);
    };
    document.addEventListener("click", elemento._co);
  },
  unmounted(elemento) {
    document.removeEventListener("click", elemento._co);
  },
};
</script>

<style scoped>
/* En viewports angostos el modal centrado quedaba pegado (a veces debajo)
   del navbar fijo; se empuja un poco hacia abajo en todos los tamaños. */
.modal-dialog {
  margin-top: 4.5rem;
}

.modal-divisan {
  background-color: var(--d-cream);
  border: 1.5px solid rgba(64, 34, 8, 0.2);
  border-radius: 18px;
}
.modal-header {
  padding: 1.1rem 1.4rem 0.8rem;
  border-bottom: 0.5px solid rgba(64, 34, 8, 0.12) !important;
}
.header-titulo {
  font-size: 1.05rem;
  font-weight: 700;
  color: var(--d-brown);
  margin: 0;
}
.modal-body {
  padding: 1rem 1.4rem;
}

/* ── Campos ─────────────────────────────────────────────── */
.campo-label {
  display: block;
  font-size: 0.76rem;
  font-weight: 700;
  color: var(--d-green);
  text-transform: uppercase;
  letter-spacing: 0.03em;
  margin-bottom: 5px;
}
.campo-input {
  width: 100%;
  border: 1px solid rgba(96, 108, 56, 0.4);
  border-radius: 8px;
  padding: 7px 12px;
  font-size: 0.86rem;
  color: var(--d-brown);
  background: white;
  outline: none;
  box-sizing: border-box;
}
.campo-input:focus {
  border-color: var(--d-green-dark);
  box-shadow: 0 0 0 3px rgba(40, 54, 24, 0.1);
}
.campo-input:disabled {
  opacity: 0.55;
}
.contador {
  float: right;
  font-size: 0.72rem;
  color: rgba(64, 34, 8, 0.5);
  text-transform: none;
}
.contador.tope {
  color: #c62828;
}

/* ── Dropdown cliente ───────────────────────────────────── */
.select-wrapper {
  position: relative;
}
.select-input.tiene-valor::placeholder {
  color: var(--d-brown);
  opacity: 1;
}
.select-arrow {
  position: absolute;
  right: 11px;
  top: 50%;
  transform: translateY(-50%);
  cursor: pointer;
  color: var(--d-green);
}
.dropdown-lista {
  position: absolute;
  z-index: 30;
  top: calc(100% + 3px);
  left: 0;
  right: 0;
  background: white;
  border: 1px solid rgba(96, 108, 56, 0.35);
  border-radius: 8px;
  max-height: 220px;
  overflow-y: auto;
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.12);
}
/* Estado: 5 renglones visibles (30px c/u); de ahí en adelante, scroll */
.dropdown-estado {
  max-height: 150px;
}
.dropdown-item-cli {
  display: flex;
  gap: 8px;
  align-items: baseline;
  padding: 7px 12px;
  font-size: 0.83rem;
  cursor: pointer;
}
.dropdown-item-cli:hover,
.dropdown-item-cli.selected {
  background: rgba(96, 108, 56, 0.12);
}
.dropdown-vacio {
  padding: 10px 12px;
  font-size: 0.82rem;
  color: rgba(64, 34, 8, 0.5);
}
.item-codigo {
  font-family: var(--d-font-mono);
  font-size: 0.74rem;
  font-weight: 700;
  color: var(--d-green);
}
.item-nombre {
  color: var(--d-brown);
}

.aviso-tel {
  display: block;
  margin-top: 4px;
  font-size: 0.73rem;
  color: #c62828;
}
.aviso-tel-ok {
  display: block;
  margin-top: 4px;
  font-size: 0.73rem;
  color: rgba(64, 34, 8, 0.5);
}

/* ── Switch venta al público ────────────────────────────── */
.switch-venta-publico {
  display: flex;
  align-items: center;
  gap: 6px;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
}
.switch-venta-publico:disabled {
  cursor: not-allowed;
  opacity: 0.55;
}
.switch-track {
  position: relative;
  display: inline-block;
  width: 34px;
  height: 18px;
  border-radius: 10px;
  background: rgba(96, 108, 56, 0.3);
  transition: background 0.15s;
}
.switch-thumb {
  position: absolute;
  top: 2px;
  left: 2px;
  width: 14px;
  height: 14px;
  border-radius: 50%;
  background: white;
  transition: transform 0.15s;
}
.switch-venta-publico.activo .switch-track {
  background: var(--d-green-dark);
}
.switch-venta-publico.activo .switch-thumb {
  transform: translateX(16px);
}
.switch-txt {
  font-size: 0.74rem;
  font-weight: 600;
  color: var(--d-green);
  text-transform: uppercase;
  letter-spacing: 0.02em;
}

/* ── Teléfono con select de lada (contacto de venta al público) ─ */
.telefono-lada {
  display: flex;
  align-items: stretch;
  border: 1px solid rgba(96, 108, 56, 0.4);
  border-radius: 8px;
  overflow: hidden;
  background: white;
}
.lada-select {
  appearance: none;
  -webkit-appearance: none;
  background: rgba(96, 108, 56, 0.07)
    url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='8' height='5' viewBox='0 0 8 5'%3E%3Cpath fill='%23606c38' d='M0 0l4 5 4-5z'/%3E%3C/svg%3E")
    no-repeat right 7px center;
  border: none;
  border-right: 1px solid rgba(96, 108, 56, 0.25);
  border-radius: 0;
  padding: 0 22px 0 8px;
  font-size: 0.8rem;
  font-weight: 700;
  color: var(--d-brown);
}
.lada-select:focus {
  outline: none;
}
.telefono-lada .tel-input {
  border: none;
  border-radius: 0;
  flex: 1;
  box-shadow: none !important;
}

/* ── Zona de carga ──────────────────────────────────────── */
.aviso-reemplazo {
  background: rgba(21, 101, 192, 0.07);
  border: 1px solid rgba(21, 101, 192, 0.25);
  border-radius: 8px;
  padding: 8px 12px;
  font-size: 0.78rem;
  color: #1565c0;
  margin-bottom: 8px;
}
.upload-zone {
  border: 2px dashed rgba(96, 108, 56, 0.4);
  border-radius: 12px;
  padding: 20px;
  text-align: center;
  cursor: pointer;
  transition: all 0.15s;
}
.upload-zone:hover:not(.deshabilitado),
.upload-zone.dragging {
  border-color: var(--d-green-dark);
  background: rgba(96, 108, 56, 0.05);
}
.upload-zone.deshabilitado {
  opacity: 0.5;
  cursor: not-allowed;
}
.upload-inner {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 14px;
}
.upload-icon {
  font-size: 1.7rem;
  color: var(--d-green);
}

.lista-archivos {
  margin-top: 10px;
}
.archivo-row {
  display: flex;
  align-items: center;
  gap: 8px;
  background: white;
  border: 1px solid rgba(96, 108, 56, 0.2);
  border-radius: 8px;
  padding: 6px 10px;
  margin-bottom: 5px;
}
/* Los ya guardados se distinguen de los pendientes de subir */
.archivo-row.guardado {
  background: rgba(96, 108, 56, 0.06);
}
.archivo-nombre {
  flex: 1;
  font-size: 0.82rem;
  color: var(--d-brown);
  word-break: break-all;
  text-decoration: none;
}
a.archivo-nombre:hover {
  color: var(--d-green);
  text-decoration: underline;
}
.archivo-size {
  font-size: 0.72rem;
  color: rgba(64, 34, 8, 0.5);
  white-space: nowrap;
}
.btn-quitar {
  background: none;
  border: none;
  color: rgba(64, 34, 8, 0.45);
  cursor: pointer;
  padding: 0 3px;
}
.btn-quitar:hover:not(:disabled) {
  color: #c62828;
}

.error-box {
  display: flex;
  align-items: flex-start;
  margin-top: 10px;
  font-size: 0.8rem;
  color: #c62828;
}

/* ── Footer ─────────────────────────────────────────────── */
.modal-footer {
  border-top: 0.5px solid rgba(64, 34, 8, 0.12) !important;
  padding: 0.85rem 1.4rem;
}
.btn-cancelar {
  background: none;
  border: 1px solid rgba(96, 108, 56, 0.4);
  color: var(--d-green);
  font-weight: 600;
  border-radius: 8px;
  font-size: 0.85rem;
}
.btn-guardar {
  background: var(--d-brown);
  color: var(--d-cream);
  border: none;
  font-weight: 700;
  border-radius: 8px;
  font-size: 0.85rem;
}
.btn-guardar:hover:not(:disabled) {
  background: var(--d-green-dark);
  color: var(--d-cream);
}
.btn-guardar:disabled,
.btn-cancelar:disabled {
  opacity: 0.45;
}
</style>
